/**
 * Setup checklist (dashboard) — dismiss + reopen.
 *
 * Phase 21.D migration: replaces the inline ``dismissChecklist()`` /
 * ``reopenChecklist()`` functions that used to live in
 * ``templates/office/dashboard/_setup_checklist.html`` and were wired
 * via raw ``onclick=`` attributes on the X / "Finish setup" buttons.
 *
 * Contract
 * --------
 *
 * - Triggers carry one of:
 *     * ``[data-checklist-dismiss]`` — collapses the full card into a
 *       small "Finish setup (N remaining)" link.
 *     * ``[data-checklist-reopen]`` — flips the user state back to
 *       "checklist visible" and soft-reloads the dashboard so the full
 *       card re-renders (we don't have the server-side HTML on the
 *       client at this point, and the dashboard has many other
 *       concurrent HTMX widgets that we don't want to disturb).
 *
 * - The CSRF token is read from any ``[name=csrfmiddlewaretoken]``
 *   input on the page (Django's standard pattern). Falls back to the
 *   ``csrftoken`` cookie so the delegate works even on pages that
 *   don't render a form.
 *
 * - URLs are read from ``data-url`` on the trigger so the template
 *   stays the single source of truth for the endpoints
 *   (``office:dismiss_setup_checklist`` / ``office:reopen_setup_checklist``).
 *
 * - The dismiss success path replaces the full card with a minimal
 *   "Finish setup (N remaining)" snippet built client-side. The
 *   trigger carries ``data-collapsed-label`` and ``data-remaining``
 *   so the template's translated copy survives the swap (Django
 *   ``{% trans %}`` happens at render-time, the JS just splices the
 *   pre-translated string into the snippet). The reopen button
 *   inside the new snippet is then re-bound for free by the body-
 *   delegated listener — no extra work needed.
 *
 * - Idempotency: bound ONCE per SPA lifetime via module-level
 *   ``_bound`` flag. The dashboard is full-page swappable
 *   (boost back/forward), but the body-level delegate survives
 *   every swap so we never re-bind.
 */

let _bound = false;

function getCsrfToken() {
  const input = document.querySelector('[name=csrfmiddlewaretoken]');
  if (input && input.value) return input.value;
  const cookies = document.cookie.split(';');
  for (let i = 0; i < cookies.length; i += 1) {
    const c = cookies[i].trim();
    if (c.startsWith('csrftoken=')) return c.substring(10);
  }
  return '';
}

function postCsrf(url) {
  return fetch(url, {
    method: 'POST',
    credentials: 'same-origin',
    headers: {
      'X-CSRFToken': getCsrfToken(),
      'X-Requested-With': 'XMLHttpRequest',
    },
  });
}

function escapeHtml(text) {
  const div = document.createElement('div');
  div.textContent = text == null ? '' : String(text);
  return div.innerHTML;
}

function buildCollapsedSnippet(label, remaining, remainingWord, reopenUrl) {
  const safeLabel = escapeHtml(label);
  const safeWord = escapeHtml(remainingWord);
  const safeUrl = escapeHtml(reopenUrl);
  return (
    '<div id="setup-checklist-collapsed" class="mb-4">' +
      '<button type="button" data-checklist-reopen ' +
        'data-url="' + safeUrl + '" ' +
        'class="inline-flex items-center gap-1.5 text-sm text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 hover:underline transition">' +
        '<svg class="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">' +
          '<path d="M18,8.5a2.5,2.5,0,0,1-5,0A2.5,2.5,0,0,1,18,8.5Zm-.006,6.866a11.065,11.065,0,0,1-1.163,4.569A7.634,7.634,0,0,1,10,24H9V18.5A3.517,3.517,0,0,0,5.5,15H0V14A7.634,7.634,0,0,1,4.065,7.169,11.065,11.065,0,0,1,8.634,6.006,15.487,15.487,0,0,1,20.972,0h0A3.009,3.009,0,0,1,24,3,15.507,15.507,0,0,1,17.994,15.366ZM2.084,13H4.346A34.361,34.361,0,0,1,6.955,8.237a8.993,8.993,0,0,0-1.993.72A5.519,5.519,0,0,0,2.084,13Zm13.679,4.045A34.361,34.361,0,0,1,11,19.654v2.262a5.519,5.519,0,0,0,4.043-2.878A8.993,8.993,0,0,0,15.763,17.045ZM22,2.972A1,1,0,0,0,21,2c-5.16.147-8.65,2.124-12.018,6.822a29.92,29.92,0,0,0-2.471,4.271,5.5,5.5,0,0,1,4.4,4.4,29.92,29.92,0,0,0,4.271-2.471C19.876,11.65,21.853,8.16,22,2.972ZM6.122,17.879a3.015,3.015,0,0,1,0,4.242c-.907.906-3.622,1.465-4.748,1.664l-1.406.247.247-1.406c.2-1.126.758-3.841,1.664-4.748A3.073,3.073,0,0,1,6.122,17.879ZM5,20a.993.993,0,0,0-.293-.707,1,1,0,0,0-1.414,0A7.318,7.318,0,0,0,2.5,21.5a7.342,7.342,0,0,0,2.208-.794A.993.993,0,0,0,5,20Z"/>' +
        '</svg>' +
        safeLabel + ' (' + Number(remaining) + ' ' + safeWord + ')' +
      '</button>' +
    '</div>'
  );
}

function handleDismiss(trigger) {
  const url = trigger.getAttribute('data-url');
  if (!url) return;
  const card = document.getElementById('setup-checklist');
  const label = trigger.getAttribute('data-collapsed-label') || 'Finish setup';
  const remaining = trigger.getAttribute('data-remaining') || '0';
  const remainingWord = trigger.getAttribute('data-remaining-word') || 'remaining';
  const reopenUrl = trigger.getAttribute('data-reopen-url') || '';

  postCsrf(url).then(() => {
    if (!card) return;
    card.outerHTML = buildCollapsedSnippet(label, remaining, remainingWord, reopenUrl);
  }).catch(() => { /* swallow — UI stays in current state */ });
}

function handleReopen(trigger) {
  const url = trigger.getAttribute('data-url');
  if (!url) return;
  postCsrf(url).then(() => {
    if (typeof window._softReload === 'function') {
      window._softReload();
    } else {
      window.location.reload();
    }
  }).catch(() => { /* swallow */ });
}

export function initSetupChecklist() {
  if (_bound) return;
  _bound = true;
  document.body.addEventListener('click', (event) => {
    const dismiss = event.target.closest('[data-checklist-dismiss]');
    if (dismiss) {
      event.preventDefault();
      handleDismiss(dismiss);
      return;
    }
    const reopen = event.target.closest('[data-checklist-reopen]');
    if (reopen) {
      event.preventDefault();
      handleReopen(reopen);
    }
  });
}

export function autoInitSetupChecklist() {
  initSetupChecklist();
}
